<template>
  <div class="space-y-5">
    <section class="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div v-if="loading" class="py-10 text-center text-sm text-slate-500">加载中...</div>
      <div v-else-if="!apply.id" class="py-10 text-center text-sm text-slate-500">未找到申请</div>
      <div v-else class="space-y-5">
        <div class="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <div class="font-mono text-sm text-slate-500">{{ apply.applyNo }}</div>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              <WealthStatusBadge type="operation" :value="apply.operationType" />
              <WealthStatusBadge type="apply" :value="apply.status" show-dot />
            </div>
          </div>
          <div class="text-right">
            <div class="text-xs text-slate-500">审批实例</div>
            <div class="font-mono text-sm text-slate-900">{{ apply.approvalInstanceId || '-' }}</div>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
          <FieldBlock label="申请金额" :value="`¥${formatMoney(apply.amount)}`" strong />
          <FieldBlock label="续期本金" :value="`¥${formatMoney(apply.rolloverPrincipalAmount)}`" strong />
          <FieldBlock label="客户名称" :value="customerDisplayName" />
          <FieldBlock label="合同名称" :value="contractDisplayName" />
          <FieldBlock label="目标批次" :value="apply.targetBatchNoSnapshot || apply.targetBatchId || '-'" />
          <FieldBlock label="年利率（%）" :value="formatRate(apply.annualRate)" />
          <FieldBlock label="起息日" :value="formatDate(apply.valueDate)" />
          <FieldBlock label="到期日" :value="formatDate(apply.maturityDate)" />
          <FieldBlock label="申请人" :value="apply.applicantName || apply.applicantUserId || '-'" />
          <FieldBlock label="申请时间" :value="formatDateTime(apply.applyTime)" />
          <FieldBlock label="审批结果时间" :value="formatDateTime(apply.reviewResultTime)" />
          <FieldBlock label="落账时间" :value="formatDateTime(apply.accountedTime)" />
        </div>

        <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div class="text-xs font-medium text-slate-500">说明</div>
          <div class="mt-2 whitespace-pre-wrap text-sm text-slate-700">{{ apply.description || '-' }}</div>
        </div>

        <div v-if="failureReason" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {{ failureReason }}
        </div>
      </div>
    </section>

    <section class="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div class="border-b border-slate-200 px-4 py-3">
        <h3 class="text-sm font-semibold text-slate-900">批次明细</h3>
      </div>
      <div class="data-table-shell border-0 shadow-none">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">批次</th>
                <th class="data-table-th text-right">金额</th>
                <th class="data-table-th text-right">提交前本金</th>
                <th class="data-table-th text-left">起止日期</th>
                <th class="data-table-th text-right">年利率（%）</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="detailLoading">
                <td colspan="5" class="data-table-empty">加载中...</td>
              </tr>
              <tr v-else-if="details.length === 0">
                <td colspan="5" class="data-table-empty">暂无明细</td>
              </tr>
              <tr v-for="item in details" v-else :key="item.id" class="data-table-row">
                <td class="data-table-td">
                  <div class="font-mono text-xs text-slate-900">{{ item.batchNoSnapshot || item.batchId || '-' }}</div>
                </td>
                <td class="data-table-td is-right">¥{{ formatMoney(item.amount) }}</td>
                <td class="data-table-td is-right">¥{{ formatMoney(item.beforeBatchPrincipalSnapshot) }}</td>
                <td class="data-table-td">{{ formatDate(item.valueDateSnapshot) }} 至 {{ formatDate(item.maturityDateSnapshot) }}</td>
                <td class="data-table-td is-right">{{ formatRate(item.annualRateSnapshot) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <WealthAttachmentPanel
      v-if="apply.id"
      title="申请附件"
      biz-type="OPERATION_APPLY"
      :biz-id="apply.id"
      :can-manage="false"
    />
  </div>
</template>

<script setup>
import { computed, defineComponent, h, ref, watch } from 'vue'
import { getWealthOperationApply, getWealthOperationApplyBatchDetails } from '@/api/wealth'
import WealthAttachmentPanel from './WealthAttachmentPanel.vue'
import WealthStatusBadge from './WealthStatusBadge.vue'
import { formatDate, formatDateTime, formatMoney, formatRate } from '../utils'

const FieldBlock = defineComponent({
  name: 'FieldBlock',
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], default: '-' },
    strong: { type: Boolean, default: false }
  },
  setup(props) {
    return () => h('div', [
      h('div', { class: 'text-xs text-slate-500' }, props.label),
      h('div', { class: props.strong ? 'mt-1 font-semibold text-slate-900' : 'mt-1 text-sm text-slate-700' }, props.value || '-')
    ])
  }
})

const props = defineProps({
  applyId: {
    type: [Number, String],
    default: null
  }
})

const loading = ref(false)
const detailLoading = ref(false)
const apply = ref({})
const details = ref([])

const failureReason = computed(() => {
  return apply.value.accountFailedReason || apply.value.approvalStartFailedReason || ''
})

const customerDisplayName = computed(() => {
  return apply.value.wealthCustomerName || apply.value.customerName || '-'
})

const contractDisplayName = computed(() => {
  if (apply.value.contractName && apply.value.contractNo) {
    return `${apply.value.contractName}（${apply.value.contractNo}）`
  }
  return apply.value.contractName || apply.value.contractNo || '-'
})

async function loadApply() {
  const id = Number(props.applyId)
  if (!Number.isFinite(id) || id <= 0) {
    apply.value = {}
    details.value = []
    return
  }
  loading.value = true
  detailLoading.value = true
  try {
    const [applyResult, detailResult] = await Promise.all([
      getWealthOperationApply(id),
      getWealthOperationApplyBatchDetails(id)
    ])
    apply.value = applyResult || {}
    details.value = detailResult || []
  } finally {
    loading.value = false
    detailLoading.value = false
  }
}

watch(() => props.applyId, loadApply, { immediate: true })
</script>
